import { useTranslation } from 'react-i18next';
import { RunListDto } from '@/Domain/api/Simulation/dto/Simulation.dto';
import { Badge } from '@/Domain/components/Shared/Badge/Badge';
import { StatusBadge } from '@/UI/Simulation/components/StatusBadge';
import { getScoreColor } from '@/UI/Simulation/components/simulationUtils';

type Props = {
  run: RunListDto;
  onClick: () => void;
};

const OVERRIDE_LABELS: Record<string, Record<number, string>> = {
  creativity_level: { 0: 'Precise', 1: 'Balanced', 2: 'Creative' },
  reply_length: { 0: 'Short', 1: 'Medium', 2: 'Long' },
};

// Mirrors the backend allowlist in quickchat/core/implementation_config.py.
// Anything outside this set was not an actual override and must not render
// here — otherwise stray metadata leaks into the run card as a raw badge.
const RENDERABLE_OVERRIDE_KEYS = new Set([
  'personality',
  'profession',
  'creativity_level',
  'reply_length',
  'language_chosen',
  'short_description',
  'one_word_description',
  'ai_commands',
]);

export const formatOverride = (key: string, value: unknown): string => {
  const labels = OVERRIDE_LABELS[key];
  if (labels && typeof value === 'number' && value in labels) return labels[value];
  if (key === 'ai_commands' && Array.isArray(value))
    return `${value.length} command${value.length !== 1 ? 's' : ''}`;
  return String(value);
};

export const RunCard = ({ run, onClick }: Props) => {
  const { t } = useTranslation('simulation_page');
  const progress = run.total_messages > 0 ? (run.completed_messages / run.total_messages) * 100 : 0;
  const overrideEntries = Object.entries(run.config_overrides).filter(([key]) =>
    RENDERABLE_OVERRIDE_KEYS.has(key),
  );

  return (
    <button
      data-testid={`run-card-${run.id}`}
      onClick={onClick}
      className="flex w-full cursor-pointer items-center justify-between rounded-lg border bg-card p-4 text-left transition-colors hover:bg-accent/50"
    >
      <div className="min-w-0 flex-1">
        <div className="flex min-h-6 items-center gap-2">
          <p data-testid={`run-card-label-${run.id}`} className="text-sm font-medium">
            {run.label || `Run #${run.ord_number}`}
          </p>
          {run.status !== 'completed' && <StatusBadge status={run.status} />}
          {run.avg_eval_score != null && (
            <span
              data-testid={`run-card-score-${run.id}`}
              className={`text-xs font-semibold ${getScoreColor(run.avg_eval_score)}`}
            >
              {run.avg_eval_score.toFixed(1)}/5
            </span>
          )}
          {run.manual_avg_score != null && (
            <span className="text-xs text-muted-foreground">
              {Math.round((run.manual_avg_score / 5) * 100)}%
            </span>
          )}
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          {run.completed_messages}/{run.total_messages} {t('tab_messages').toLowerCase()}
          {run.failed_messages > 0 &&
            ` (${run.failed_messages} ${t('failed_label').replace(':', '').toLowerCase()})`}
        </p>
        {overrideEntries.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {overrideEntries.map(([key, value]) => (
              <Badge key={key} variant="secondary" className="text-xs font-normal">
                {formatOverride(key, value)}
              </Badge>
            ))}
          </div>
        )}
        <div
          data-testid={`run-card-progress-${run.id}`}
          className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted"
          style={{ visibility: run.status === 'running' ? 'visible' : 'hidden' }}
        >
          <div
            className="h-full rounded-full bg-info transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </button>
  );
};
